<script setup lang="ts">
import {QuestionType, type Questionnaire} from '@/ts/types/dto/interventions-dto';
import {computed, onBeforeMount, ref, watch} from 'vue';
import {
  AnswerComponentType,
  type QuestionnaireAnswers,
  type SessionRequestQuestionnaire,
} from '@/ts/types/dto/session-request-dto';
import QuestionComponent from '@/vue/molecules/components/question/question-component.vue';
import {useLogger} from '@/ts/composables/pure/use-logger';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import BalmProgressCount from '@/vue/atoms/balm-progress-count.vue';
import QuestionnaireScore from '@/vue/molecules/components/questionnaire/questionnaire-score.vue';
import BalmSlider from '@/vue/atoms/balm-slider.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';

const props = defineProps<{
  questionnaire: Questionnaire;
  loading: boolean;
  embedded?: boolean; // if true, this component renders a plain div instead of the BalmModal-component
  readonly?: boolean; // if true, prevents editing of completed assessments
  /**
   * Whether the answers of this pass have been accepted. Only the surface that owns the overlay
   * knows: this component asks to submit and is told nothing about how it went. It closes the way
   * back off the result, since a pass already filed cannot be edited — and, just as deliberately,
   * leaves it open while this is false, which is where a submission that failed is retried from.
   *
   * Left out by a caller that cannot say, and then nothing here changes.
   */
  submitted?: boolean;
}>();

const emit = defineEmits<{
  update: [];
  finish: [];
}>();

const model = defineModel<SessionRequestQuestionnaire[] | null>();
const isModalOpen = defineModel<boolean>('isModalOpen');

const currentQuestionIndex = ref(0);
const logger = useLogger();
const totalSteps = computed(() => {
  if (props.questionnaire.hasScore) {
    return props.questionnaire.questions.length + 1;
  }
  return props.questionnaire.questions.length;
});
const insideQuestionnaire = computed(() => !(props.questionnaire.hasScore && currentQuestionIndex.value === totalSteps.value - 1));

const value = computed<QuestionnaireAnswers | null>({
  get() {
    return model.value?.[currentQuestionIndex.value]?.questionnaireAnswers ?? null;
  },
  set(newValue) {
    if (!newValue) {
      return;
    }
    const res = [...(model.value ?? [])];
    res[currentQuestionIndex.value] = {
      questionnaireAnswers: newValue,
      type: AnswerComponentType.QUESTIONNAIRE,
    };
    model.value = res;
  },
});

function save(): void {
  if (props.readonly) {
    // IPD-2772: completed questionnaires must not be modified. The readonly
    // branch renders no inputs or submit buttons; this guard stays as defense
    // in depth against any other path into save (e.g. Enter-key form submit).
    return;
  }
  logger.debug('Saving questionnaire')();
  currentQuestionIndex.value++;
  if (currentQuestionIndex.value === props.questionnaire.questions.length) {
    emit('update');
    if (!props.questionnaire.hasScore) {
      emit('finish');
      currentQuestionIndex.value--; // roll back from outside right value
    }
  } else if (currentQuestionIndex.value > props.questionnaire.questions.length) {
    emit('finish');
    currentQuestionIndex.value--; // roll back from outside right value
  }
}

/**
 * Where a pass starts, which is always the first question.
 *
 * It used to open on the last step whenever ANY question came back answered, and that is only
 * sound for a questionnaire being read back — which is what the readonly branch below draws, and
 * it reads no index at all. Editable, it is a trap: the answers this modal collects start empty in
 * every one of its callers (they are cleared as the questionnaire is fetched), so the steps that
 * were skipped hold nothing, while the question on screen offers the previous answer back and
 * makes the whole thing look ready to submit. What leaves is one answer for a questionnaire the
 * answer endpoint takes whole — it wants an entry per question and refuses a set missing any — so
 * a reassessment off its cooldown, or a questionnaire left half-done, ended on an error the user
 * could do nothing about.
 *
 * Starting at the top costs the user the steps they answered before, but each question still
 * arrives with its previous answer selected, so a reassessment is a walk through what they said
 * last time rather than a blank form.
 */
function resetQuestionIndex(): void {
  currentQuestionIndex.value = 0;
}

onBeforeMount(() => {
  logger.debug(`onBeforeMont ${props.questionnaire.id}`)();
  resetQuestionIndex();
});

// Reset question index when modal reopens (handles case where q-dialog
// preserves the component instance instead of remounting)
watch(isModalOpen, (val) => {
  if (val) {
    resetQuestionIndex();
  }
});

// And when the questionnaire changes under a modal that stays mounted, which neither of the above
// covers. The id and not the object: a submitted one comes back from its read-back as a new object
// under the same id, carrying the score the last step draws.
watch(() => props.questionnaire.id, resetQuestionIndex);

const dialogProps = computed(() => {
  return {
    type: 'h900',
    theme: 'grey-10',
    noScroll: 'no-scroll',
    modelValue: isModalOpen.value, // Add modelValue here
    'onUpdate:modelValue': (newModelValue: boolean): void => {isModalOpen.value = newModelValue;},
  };
});

const canContinue = computed(() => {
  // If we're on the score/results page (not inside questionnaire), always allow continue
  if (!insideQuestionnaire.value) {
    return true;
  }

  if (!value.value) {
    return false;
  }

  const currentQuestion = props.questionnaire.questions[currentQuestionIndex.value];
  if (!currentQuestion) {
    return false;
  }

  // For numeric questions, check if the numeric value exists
  if (currentQuestion.type === QuestionType.NUMERIC && value.value.answers && 'numeric' in value.value.answers) {
    return value.value.answers.numeric !== null && typeof value.value.answers.numeric !== 'undefined';
  }

  // Multichoice: keep Continue enabled as soon as the user has started answering
  // (>= 1 selected) so that submitting surfaces the question's specific validation
  // message (e.g. "Please select 2 answers") when the min/max isn't met yet. The
  // strict min/max + custom-answer rule is enforced by the question's form-required
  // (isMultiChoiceSelectionValid), which blocks the submit until satisfied.
  if (currentQuestion.type === QuestionType.MULTICHOICE && value.value.answers && 'multiChoice' in value.value.answers) {
    return value.value.answers.multiChoice.length > 0;
  }

  // For other question types, check if answers exists and is not null
  return value.value.answers !== null && typeof value.value.answers !== 'undefined';
});

</script>

<template>
  <component
    :is="embedded ? 'div' : BalmDialog"
    v-bind="embedded ? {} : dialogProps"
    :class="embedded ? 'embedded-questionnaire-standard-modal' : ''"
  >
    <div class="questionnaire-standard-modal">
      <q-form class="form" @submit="save">
        <div class="headline">
          <h2 class="text-heading-2 headline-title">
            {{ questionnaire.name }}
          </h2>
          <svg-illustration name="right-down-arrow.svg" class="headline-icon"/>
        </div>
        <!-- IPD-2772: completed questionnaires render their results and interpretation
             as static, screen-reader-accessible content instead of the stepper. The
             saved answers are intentionally NOT shown here — they belong only to the
             session-completed recap (read-only-questionnaire-standard). -->
        <div
          v-if="readonly"
          class="readonly-answers"
          tabindex="0"
          role="region"
          :aria-label="questionnaire.name"
        >
          <questionnaire-score v-if="questionnaire.score" :score="questionnaire.score"/>
          <!-- Scoreable but score missing (anomaly) vs. structurally unscored
               (e.g. check-ins) read differently: only the former implies a
               result should exist -->
          <p v-else-if="questionnaire.hasScore">
            {{ $t('score_unavailable') }}
          </p>
          <p v-else>
            {{ $t('questionnaire_already_completed') }}
          </p>
        </div>
        <div v-else-if="insideQuestionnaire" class="question">
          <balm-slider :slide-index="currentQuestionIndex">
            <div class="question-center">
              <question-component
                v-model="value"
                :questionnaire-id="questionnaire.id"
                :questionnaire-type="questionnaire.type"
                :question="questionnaire.questions[currentQuestionIndex]"
                :display-list-with-question-in-row="true"
                @ready-to-go-next="save"
              />
            </div>
          </balm-slider>
        </div>
        <div v-else class="score">
          <q-skeleton v-if="loading" class="loading"/>
          <questionnaire-score v-else-if="questionnaire.score" :score="questionnaire.score"/>
          <!-- Reached only through `hasScore`, so a missing score is an anomaly worth naming rather
               than an empty panel — the same line the readonly branch draws for it. -->
          <p v-else>
            {{ $t('score_unavailable') }}
          </p>
        </div>
        <div v-if="!readonly" class="footer">
          <div class="footer-progress">
            <balm-progress-count :total="totalSteps" :current="currentQuestionIndex"/>
          </div>
          <div class="footer-btns">
            <!-- Disabled on the result of a pass that landed, as well as on the first question:
                 that pass cannot be edited, so stepping back off it would only collect an answer
                 with nowhere to go. NOT disabled on the result of a submission that failed, which
                 looks identical from here and is where the retry comes from — the caller is what
                 tells the two apart. Disabled while one is travelling for the same reason:
                 `submitted` cannot be true yet, so that is the window it would step back into. -->
            <balm-btn
              :disable="currentQuestionIndex === 0 || loading || (!insideQuestionnaire && Boolean(submitted))"
              icon="arrow-left.svg"
              theme="grey"
              compact
              :aria-label="$t('aria_question_back_btn')"
              @click="currentQuestionIndex--"
            />
            <balm-btn
              v-if="totalSteps - 1 !== currentQuestionIndex"
              :aria-label="$t('aria_question_next_btn')"
              type="submit"
              :label="$t('continue')"
              icon="arrow-right.svg"
              :disable="!canContinue"
            />
            <balm-btn
              v-else
              type="submit"
              :loading="loading"
              :label="$t('complete_assessment')"
              icon="check.svg"
              theme="green"
              :disable="loading || !canContinue"
            />
          </div>
        </div>
      </q-form>
    </div>
  </component>
</template>
<style scoped lang="scss">
.embedded-questionnaire-standard-modal {
  height: 100%;
}

.questionnaire-standard-modal {
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
}

.form {
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr auto;
}

.headline {
  padding: var(--Spacing-spacing-7);
  padding-bottom: var(--Spacing-spacing-4);
  padding-right: calc(var(--Spacing-spacing-7) + 56px);
  display: flex;
  align-items: flex-end;
  gap: var(--Spacing-spacing-4);
}

.headline-title {
  margin: 0;
}

.headline-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;

  @include screen-md {
    width: 20px;
    height: 20px;
  }
  @include screen-lg {
    width: 24px;
    height: 24px;
  }
}

.question {
  min-height: 0;
  overflow: hidden;
}

.readonly-answers {
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
  padding: 0 var(--Spacing-spacing-7) var(--Spacing-spacing-7);
}

.question-center {
  padding: 0 var(--Spacing-spacing-7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100%;

  @include screen-md {
    padding: 0 var(--Spacing-spacing-8) 0 var(--Spacing-spacing-7);
  }

  // Stretch the question wrapper to the full question area so an intro
  // (when present) pins to the top, right below the modal headline, while
  // the question itself stays vertically centered in the remaining space.
  :deep(> .question-component) {
    flex: 1;
  }
}

.score {
  overflow-y: auto;
  padding: 0 var(--Spacing-spacing-7);

  @include screen-md {
    padding-top: 80px;
  }

  .loading {
    height: 100%;
  }
}

.footer {
  display: flex;
  align-items: flex-end;
  padding: var(--Spacing-spacing-6) var(--Spacing-spacing-7) var(--Spacing-spacing-7);

  @include screen-md {
    padding: var(--Spacing-spacing-6) var(--Spacing-spacing-8) var(--Spacing-spacing-7) var(--Spacing-spacing-7);
  }
}

.footer-progress {
  flex: 1;
  display: flex;
  align-items: center;

  :deep(.balm-progress) {
    display: none;

    @include screen-md {
      display: block;
    }
  }
}

.footer-btns {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-3);

  :deep(.balm-btn.compact) {
    padding: 0 var(--Spacing-spacing-6) !important;
  }

  :deep(.balm-btn:not(.compact)) {
    padding: var(--Spacing-spacing-3) var(--Spacing-spacing-6) !important;
    min-height: unset !important;
    height: 40px;

    @include screen-md {
      height: 48px;
    }

    @include screen-lg {
      height: 56px;
    }
  }

  :deep(.icon-holder svg) {
    width: 16px;
    height: 16px;

    @include screen-md {
      width: 20px;
      height: 20px;
    }

    @include screen-lg {
      width: 24px;
      height: 24px;
    }
  }
}
</style>
